'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useTranslations } from 'next-intl';
import { useHasMounted } from '@/hooks/use-has-mounted';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/hooks/use-auth';
import { useRegional } from '@/components/providers/regional-provider';
import { trpc } from '@/lib/trpc';
import { useDisplayCurrency } from '@/tenants/_shared/hooks';
import { useEntrantDonationStrategy } from '@/tenants/_shared/hooks/donation-strategies';
import { getTenantConfig } from '@/tenants/config';
import Image from 'next/image';
import Link from 'next/link';
import {
  MapPin,
  Calendar,
  Car,
  Heart,
  TrendingUp,
  Award,
  Users,
  Share2,
  UserPlus,
  ChevronRight,
  ChevronLeft,
  X,
  Link as LinkIcon,
  Facebook,
  Mail,
  Check,
  Copy,
  Pencil,
  Pin,
  Plus,
  Loader2,
  Trash2,
  ImagePlus,
  QrCode,
} from 'lucide-react';
import {
  DonationFormModal,
  DonationAmountCard,
  type DonationData,
} from '@/tenants/_shared/components';
import { usePaymentReturn } from '@/tenants/_shared/hooks/usePaymentReturn';
import { SponsorLogosSection } from '@/tenants/_shared/components/SponsorLogosSection';
import { BlogPostImage } from '@/tenants/_shared/components/BlogPostImage';
import { useEntrantAssetUpload } from '@/tenants/_shared/hooks/use-entrant-asset-upload';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog/dialog';
import { Input } from '@/components/ui/input/input';
import { BlogEditor } from '@/components/ui/editor/blog-editor';
import { Label } from '@/components/ui/label/label';
import { QrCodeModal } from '@/components/qr-code-modal';
import { type EntrantPublicProfile } from '@impact/types';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Shared donor interface
interface Donor {
  id: string;
  name: string;
  amount: number;
  message?: string;
  date: string;
  anonymous: boolean;
}

interface PreviousUpdate {
  id: string;
  title: string;
  content?: string;
  imageUrl?: string;
  updateType: 'blog' | 'video';
  youtubeEmbedUrl?: string;
  createdAt: string;
  campaignId: string;
  campaignName: string;
}

// Shared blog post interface
interface BlogPost {
  id: string;
  title: string;
  content?: string;
  updateType: 'blog' | 'video';
  imageUrl?: string;
  youtubeEmbedUrl?: string;
  isPinned: boolean;
  date: string;
  isDefault?: boolean;
}

// Shared gallery image interface
interface GalleryImage {
  id: string;
  url: string;
  caption?: string;
  year: number;
}

// Passenger info (for riders with a pillion)
interface Passenger {
  username: string;
  firstName: string;
  lastName: string;
  profileImage?: string;
}

// Individual fundraiser data
interface FundraiserData {
  type: 'fundraiser';
  username: string;
  firstName: string;
  lastName: string;
  profileImage?: string;
  coverImage?: string;
  bio?: string;
  yearStarted: number;
  totalRaised: number;
  totalEvents: number;
  currency: string;
  currentEvent: {
    year: number;
    city: string;
    country: string;
    date: string;
    goal: number;
    raised: number;
    eventSlug?: string;
  };
  vehicle: {
    year: string;
    make: string;
    model: string;
  };
  passenger?: Passenger;
  team?: {
    slug: string;
    name: string;
  };
  donors: Donor[];
  blogPosts: BlogPost[];
  gallery: GalleryImage[];
}

// Social media icons
const XIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const TelegramIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

// Share Component - Dropdown on desktop, Sheet on mobile
function ShareDropdown({
  url,
  title,
  campaign,
  t,
}: {
  url: string;
  title: string;
  campaign: string;
  t: (key: string, values?: Record<string, string>) => string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    {
      name: t('copyLink'),
      icon: LinkIcon,
      color: 'bg-stone-200',
      action: handleCopyLink,
    },
    {
      name: 'WhatsApp',
      icon: WhatsAppIcon,
      color: 'bg-[#25D366]',
      action: () => {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${t('shareMessage', { name: title, campaign })} ${url}`)}`,
          '_blank',
        );
        setIsOpen(false);
      },
    },
    {
      name: 'Telegram',
      icon: TelegramIcon,
      color: 'bg-[#229ED9]',
      action: () => {
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(t('shareMessage', { name: title, campaign }))}`,
          '_blank',
        );
        setIsOpen(false);
      },
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-[#1877F2]',
      action: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          '_blank',
          'width=600,height=400',
        );
        setIsOpen(false);
      },
    },
    {
      name: 'X',
      icon: XIcon,
      color: 'bg-black',
      action: () => {
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(t('shareMessage', { name: title, campaign }))}`,
          '_blank',
          'width=600,height=400',
        );
        setIsOpen(false);
      },
    },
    {
      name: 'LinkedIn',
      icon: LinkedInIcon,
      color: 'bg-[#0A66C2]',
      action: () => {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank',
          'width=600,height=400',
        );
        setIsOpen(false);
      },
    },
    {
      name: t('email'),
      icon: Mail,
      color: 'bg-stone-600',
      action: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(`${t('emailSubject')} - ${title}`)}&body=${encodeURIComponent(`${t('emailBody', { name: title })}: ${url}`)}`;
        setIsOpen(false);
      },
    },
    {
      name: t('qrCode'),
      icon: QrCode,
      color: 'bg-stone-200',
      action: () => {
        setIsOpen(false);
        setIsQrOpen(true);
      },
    },
  ];

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="text-stone-900/80 hover:text-stone-900 hover:bg-white/10 border border-white/20"
      >
        <Share2 className="w-4 h-4 lg:mr-2" />
        <span className="hidden lg:block">{t('share')}</span>
      </Button>

      {/* Desktop Dropdown */}
      {!isMobile && isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-48 bg-white/95 backdrop-blur-md border border-stone-300 rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-stone-300">
              <span className="text-sm font-medium text-stone-900">{t('share')}</span>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-stone-200 transition-colors"
              >
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>
            <div className="py-1">
              {shareOptions.map((option) => {
                const IconComponent = option.icon;
                const isCopyLink = option.name === t('copyLink');
                return (
                  <button
                    key={option.name}
                    onClick={option.action}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
                  >
                    {isCopyLink && copied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <IconComponent className="w-4 h-4" />
                    )}
                    <span>{isCopyLink && copied ? t('copied') : option.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Mobile Sheet */}
      {isMobile && (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent side="bottom" className="bg-white border-stone-200 rounded-t-2xl">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-stone-900 text-center">{t('share')}</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-3 gap-4 pb-6">
              {shareOptions.map((option) => {
                const IconComponent = option.icon;
                const isCopyLink = option.name === t('copyLink');
                return (
                  <button
                    key={option.name}
                    onClick={option.action}
                    className="flex flex-col items-center gap-2"
                  >
                    <div
                      className={`w-14 h-14 rounded-full ${option.color} flex items-center justify-center`}
                    >
                      {isCopyLink && copied ? (
                        <Check className="w-6 h-6 text-stone-900" />
                      ) : (
                        <span className="text-stone-900">
                          <IconComponent className="w-6 h-6" />
                        </span>
                      )}
                    </div>
                    <span className="text-stone-500 text-xs">
                      {isCopyLink && copied ? t('copied') : option.name}
                    </span>
                  </button>
                );
              })}
            </div>
            <Button
              variant="ghost"
              onClick={() => setIsOpen(false)}
              className="w-full text-stone-500 hover:text-stone-900 hover:bg-stone-100"
            >
              {t('cancel')}
            </Button>
          </SheetContent>
        </Sheet>
      )}

      <QrCodeModal open={isQrOpen} onOpenChange={setIsQrOpen} url={url} title={title} />
    </div>
  );
}

type ApiProfile = NonNullable<EntrantPublicProfile>;

interface FundraiserProfilePageProps {
  username?: string;
  profile?: ApiProfile;
  teamSlug?: string;
  viewType?: 'fundraiser' | 'team';
}

function ShortUrlDisplay({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(`https://${url}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-1.5 mt-1 text-stone-500 hover:text-uwr-orange transition-colors group"
    >
      <span className="text-sm">{url}</span>
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-400" />
      ) : (
        <Copy className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  );
}

function LightboxKeyHandler({
  galleryLength,
  onPrev,
  onNext,
  onClose,
}: {
  galleryLength: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
      else if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryLength, onPrev, onNext, onClose]);
  return null;
}

function isHtmlContent(content: string): boolean {
  return /<(p|br|div|h[1-6]|ul|ol|li|a|strong|em|b|i|span|img|blockquote|table)\b[^>]*\/?>/i.test(
    content,
  );
}

function stripHtml(input: string): string {
  return input
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function FundraiserProfilePage({
  username,
  profile,
  teamSlug,
  viewType = 'fundraiser',
}: FundraiserProfilePageProps) {
  const t = useTranslations('fundraiser');
  const tCommon = useTranslations('common');
  const tDonate = useTranslations('donate.form');
  const utils = trpc.useUtils();
  const { entrant: authEntrant } = useAuth({ hydrateFromQuery: true });

  // Track mount state to avoid hydration mismatch with locale-sensitive formatting
  const mounted = useHasMounted();

  // Currency from regional context
  const { currency: selectedCurrency } = useRegional();
  const currency = useDisplayCurrency();

  // Payment return detection (bank redirect return flow)
  const {
    returnDonationId,
    redirectResult,
    sessionId: returnSessionId,
    eventId: returnEventId,
    recoveryToken,
    isLocalStorageRecovery,
  } = usePaymentReturn();

  // Donation modal state — single modal instance for both mobile button and desktop card
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [donateInitialAmount, setDonateInitialAmount] = useState<number | null>(null);

  const queryUsername = profile?.username ?? username ?? '';

  const { data: fetchedProfile, isLoading: isProfileLoading } =
    trpc.publicEntrant.byUsername.useQuery(
      { username: queryUsername, currencyCode: selectedCurrency },
      {
        enabled: !!queryUsername,
        initialData: profile && profile.username === queryUsername ? profile : undefined,
      },
    );
  const resolvedProfile = fetchedProfile ?? profile ?? undefined;

  const { data: galleryData } = trpc.publicEntrant.listGallery.useQuery(
    { entrantId: resolvedProfile?.id ?? '' },
    { enabled: !!resolvedProfile?.id },
  );
  const {
    data: donorsData,
    fetchNextPage: fetchNextDonorsPage,
    hasNextPage: hasMoreDonors,
    isFetchingNextPage: isFetchingMoreDonors,
  } = trpc.publicEntrant.listDonors.useInfiniteQuery(
    { entrantId: resolvedProfile?.id ?? '', currencyCode: selectedCurrency, limit: 20 },
    {
      enabled: !!resolvedProfile?.id && resolvedProfile?.privacyShowDonors !== false,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    },
  );
  const donorsTotal = donorsData?.pages[0]?.total ?? 0;
  const loadedDonors = useMemo(
    () => donorsData?.pages.flatMap((p) => p.donors) ?? [],
    [donorsData],
  );
  const profileUsername = resolvedProfile?.username ?? (queryUsername || undefined);
  const donationEventId = resolvedProfile?.currentCampaign?.eventId || returnEventId || undefined;
  const strategy = useEntrantDonationStrategy(donationEventId, resolvedProfile?.id);

  const handleDonationSuccess = useCallback(
    (data: DonationData) => {
      if (!queryUsername) return;
      const queryInput = { username: queryUsername, currencyCode: selectedCurrency };

      // Convert donation amount from payment currency to display currency
      const displayFxRate = resolvedProfile?.fxRate ?? 1;
      const paymentFxRate = strategy.paymentConfig?.paymentConfigs[0]?.fxRate ?? 1;
      const convertedAmount =
        paymentFxRate > 0 ? data.amount * (displayFxRate / paymentFxRate) : data.amount;

      void utils.publicEntrant.byUsername.cancel(queryInput);
      utils.publicEntrant.byUsername.setData(queryInput, (old) => {
        if (!old) return old;
        const newTotal = old.totalRaised + convertedAmount;
        return {
          ...old,
          totalRaised: newTotal,
          donationCount: old.donationCount + 1,
          goalProgress:
            old.fundraisingGoal > 0 ? Math.min((newTotal / old.fundraisingGoal) * 100, 100) : 0,
        };
      });
    },
    [queryUsername, selectedCurrency, utils, resolvedProfile?.fxRate, strategy.paymentConfig],
  );

  // Refetch profile after redirect payment success (zeroDrift gives correct converted total)
  const handleRedirectSuccess = useCallback(() => {
    if (!queryUsername) return;
    void utils.publicEntrant.byUsername.invalidate({
      username: queryUsername,
      currencyCode: selectedCurrency,
    });
    void utils.publicEntrant.listDonors.invalidate();
  }, [queryUsername, selectedCurrency, utils]);

  // Determine profile data based only on real API profile data.
  const profileData = useMemo<FundraiserData | null>(() => {
    if (!resolvedProfile) return null;

    return {
      type: 'fundraiser' as const,
      username: resolvedProfile.username,
      firstName: resolvedProfile.firstName,
      lastName: resolvedProfile.lastName,
      profileImage: resolvedProfile.profileImageUrl,
      coverImage: undefined,
      bio: resolvedProfile.defaultBlog?.content,
      yearStarted: resolvedProfile.yearStarted ?? new Date().getFullYear(),
      totalRaised: resolvedProfile.lifetimeTotalRaised,
      totalEvents: resolvedProfile.totalEvents ?? 1,
      currency: resolvedProfile.currencyCode ?? 'AUD',
      currentEvent: {
        year: new Date().getFullYear(),
        city: resolvedProfile.currentEvent?.city ?? '',
        country: resolvedProfile.currentEvent?.country ?? '',
        date: resolvedProfile.currentEvent?.date ?? new Date().toISOString(),
        goal: resolvedProfile.fundraisingGoal,
        raised: resolvedProfile.totalRaised,
        eventSlug: resolvedProfile.currentCampaign?.eventSlug,
      },
      vehicle: {
        year: resolvedProfile.vehicle?.year?.toString() ?? '',
        make: resolvedProfile.vehicle?.make ?? '',
        model: resolvedProfile.vehicle?.model ?? '',
      },
      passenger: (() => {
        const linkedPassenger =
          resolvedProfile.linkedPassengers?.[0] && resolvedProfile.linkedPassengers[0].username
            ? resolvedProfile.linkedPassengers[0]
            : undefined;
        const linkedPrimaryEntrant = resolvedProfile.linkedPrimaryEntrant?.username
          ? resolvedProfile.linkedPrimaryEntrant
          : undefined;
        const ridingWith = linkedPassenger ?? linkedPrimaryEntrant;
        const ridingWithUsername = ridingWith?.username;
        if (!ridingWith || !ridingWithUsername) {
          return undefined;
        }
        return ridingWith
          ? {
              username: ridingWithUsername,
              firstName: ridingWith.firstName,
              lastName: ridingWith.lastName,
              profileImage: ridingWith.profileImageUrl,
            }
          : undefined;
      })(),
      team:
        resolvedProfile.currentCampaign?.teamName && resolvedProfile.currentCampaign?.teamHandle
          ? {
              slug: resolvedProfile.currentCampaign.teamHandle ?? '',
              name: resolvedProfile.currentCampaign.teamName,
            }
          : undefined,
      donors: loadedDonors.map((d) => ({
        id: d.id,
        name: d.name ?? '',
        amount: d.amount,
        message: d.message,
        date: d.date,
        anonymous: d.anonymous,
      })),
      blogPosts:
        resolvedProfile.blogPosts?.map((blog) => ({
          id: blog.id,
          title: blog.title,
          content: blog.content,
          imageUrl: blog.imageUrl,
          updateType: blog.updateType,
          youtubeEmbedUrl: blog.youtubeEmbedUrl,
          isPinned: blog.isPinned,
          date: blog.createdAt,
          isDefault: blog.isDefault,
        })) ??
        (resolvedProfile.defaultBlog
          ? [
              {
                id: resolvedProfile.defaultBlog.id,
                title: resolvedProfile.defaultBlog.title,
                content: resolvedProfile.defaultBlog.content,
                imageUrl: resolvedProfile.defaultBlog.imageUrl,
                updateType: resolvedProfile.defaultBlog.updateType,
                youtubeEmbedUrl: resolvedProfile.defaultBlog.youtubeEmbedUrl,
                isPinned: resolvedProfile.defaultBlog.isPinned,
                date: resolvedProfile.defaultBlog.createdAt,
                isDefault: true,
              },
            ]
          : []),
      gallery:
        galleryData?.data.map(
          (item: {
            id: string;
            mediaUrl: string;
            assetCdnUrl: string | null;
            caption: string | null;
            createdAt: Date;
          }) => ({
            id: item.id,
            url: item.assetCdnUrl ?? item.mediaUrl,
            caption: item.caption ?? undefined,
            year: new Date(item.createdAt).getFullYear(),
          }),
        ) ?? [],
    };
  }, [resolvedProfile, galleryData, loadedDonors]);

  // Update drawer + form state
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
  const [isPreviousUpdatesOpen, setIsPreviousUpdatesOpen] = useState(false);
  const [selectedPreviousUpdateId, setSelectedPreviousUpdateId] = useState<string | null>(null);
  const [blogFormMode, setBlogFormMode] = useState<'create' | 'edit'>('create');
  const [editingUpdateId, setEditingUpdateId] = useState<string | null>(null);
  const [blogUpdateTypeInput, setBlogUpdateTypeInput] = useState<'blog' | 'video'>('blog');
  const [blogTitleInput, setBlogTitleInput] = useState('');
  const [blogContentInput, setBlogContentInput] = useState('');
  const [blogImageUrlInput, setBlogImageUrlInput] = useState('');
  const [blogImageFile, setBlogImageFile] = useState<File | null>(null);
  const [blogImageUploading, setBlogImageUploading] = useState(false);
  const [blogYoutubeUrlInput, setBlogYoutubeUrlInput] = useState('');
  const [blogPinnedInput, setBlogPinnedInput] = useState(false);

  const createUpdateMutation = trpc.publicEntrant.createUpdate.useMutation({
    onSuccess: () => {
      if (profileUsername) {
        void utils.publicEntrant.byUsername.invalidate();
      }
      setIsUpdateDialogOpen(false);
    },
  });

  const updateUpdateMutation = trpc.publicEntrant.updateUpdate.useMutation({
    onSuccess: () => {
      if (profileUsername) {
        void utils.publicEntrant.byUsername.invalidate();
      }
      setIsUpdateDialogOpen(false);
    },
  });

  const deleteUpdateMutation = trpc.publicEntrant.deleteUpdate.useMutation({
    onSuccess: () => {
      if (profileUsername) {
        void utils.publicEntrant.byUsername.invalidate();
      }
      setIsDeleteConfirmDialogOpen(false);
      setIsUpdateDialogOpen(false);
      setUpdateDialogOpen(false);
      setSelectedPost(null);
    },
  });

  const duplicateUpdateMutation = trpc.publicEntrant.duplicateUpdate.useMutation({
    onSuccess: () => {
      if (profileUsername) {
        void utils.publicEntrant.byUsername.invalidate();
      }
    },
  });

  const { data: previousUpdates = [] } = trpc.publicEntrant.previousUpdates.useQuery(undefined, {
    enabled: isPreviousUpdatesOpen && !!authEntrant,
  }) as { data?: PreviousUpdate[] };
  const selectedPreviousUpdate = useMemo(
    () =>
      previousUpdates.find((u: PreviousUpdate) => u.id === selectedPreviousUpdateId) ??
      previousUpdates[0],
    [previousUpdates, selectedPreviousUpdateId],
  );

  const pinUpdateMutation = trpc.publicEntrant.pinUpdate.useMutation({
    onSuccess: () => {
      if (profileUsername) {
        void utils.publicEntrant.byUsername.invalidate();
      }
    },
  });

  // Gallery upload state
  const [isGalleryUploadOpen, setIsGalleryUploadOpen] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState(0);
  const [deleteConfirmGalleryId, setDeleteConfirmGalleryId] = useState<string | null>(null);
  const { upload: entrantUpload } = useEntrantAssetUpload();

  const addGalleryImagesMutation = trpc.publicEntrant.addGalleryImages.useMutation({
    onSuccess: () => {
      void utils.publicEntrant.listGallery.invalidate();
      setIsGalleryUploadOpen(false);
      setGalleryFiles([]);
    },
  });

  const removeGalleryImageMutation = trpc.publicEntrant.removeGalleryImage.useMutation({
    onSuccess: () => {
      void utils.publicEntrant.listGallery.invalidate();
      setDeleteConfirmGalleryId(null);
    },
  });

  async function handleGalleryUpload() {
    if (galleryFiles.length === 0 || galleryFiles.length > 10) return;
    setGalleryUploading(true);
    setGalleryUploadProgress(0);
    try {
      const assetIds: string[] = [];
      for (let i = 0; i < galleryFiles.length; i++) {
        setGalleryUploadProgress(i);
        const result = await entrantUpload({
          file: galleryFiles[i],
          assetType: 'image',
          tags: ['gallery'],
        });
        assetIds.push(result.asset.id);
      }
      setGalleryUploadProgress(galleryFiles.length);
      await addGalleryImagesMutation.mutateAsync({ assetIds });
    } finally {
      setGalleryUploading(false);
      setGalleryUploadProgress(0);
    }
  }

  function openCreateUpdate() {
    setBlogFormMode('create');
    setEditingUpdateId(null);
    setBlogUpdateTypeInput('blog');
    setBlogTitleInput('');
    setBlogContentInput('');
    setBlogImageUrlInput('');
    setBlogImageFile(null);
    setBlogYoutubeUrlInput('');
    setBlogPinnedInput(false);
    setIsUpdateDialogOpen(true);
  }

  // Auto-open create update modal when ?add-update query param is present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('add-update')) return;
    const ownProfile =
      resolvedProfile &&
      authEntrant &&
      (authEntrant.id === resolvedProfile.id ||
        authEntrant.username?.toLowerCase() === resolvedProfile.username?.toLowerCase());
    if (ownProfile) {
      openCreateUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedProfile?.id, authEntrant?.id]);

  function copyPreviousUpdateToNew(update: PreviousUpdate | undefined) {
    if (!update) return;
    setBlogFormMode('create');
    setEditingUpdateId(null);
    setBlogUpdateTypeInput(update.updateType);
    setBlogTitleInput(update.title);
    setBlogContentInput(update.content ?? '');
    setBlogImageUrlInput(update.imageUrl ?? '');
    setBlogImageFile(null);
    setBlogYoutubeUrlInput(update.youtubeEmbedUrl ?? '');
    setBlogPinnedInput(false);
    setIsPreviousUpdatesOpen(false);
    setIsUpdateDialogOpen(true);
  }

  function openEditUpdate(post: BlogPost) {
    setBlogFormMode('edit');
    setEditingUpdateId(post.id);
    setBlogUpdateTypeInput(post.updateType);
    setBlogTitleInput(post.title);
    setBlogContentInput(post.content ?? '');
    setBlogImageUrlInput(post.imageUrl ?? '');
    setBlogImageFile(null);
    setBlogYoutubeUrlInput(post.youtubeEmbedUrl ?? '');
    setBlogPinnedInput(post.isPinned);
    setIsUpdateDialogOpen(true);
  }

  async function handlePinUpdate(updateId: string) {
    await pinUpdateMutation.mutateAsync({ updateId });
  }

  async function handleDeleteUpdate() {
    if (!editingUpdateId) return;
    await deleteUpdateMutation.mutateAsync({ updateId: editingUpdateId });
  }

  async function handleDuplicateUpdate(updateId: string) {
    await duplicateUpdateMutation.mutateAsync({ updateId });
  }

  async function handleSaveUpdate() {
    if (!blogTitleInput.trim()) return;
    if (blogUpdateTypeInput === 'blog' && !blogContentInput.trim()) return;
    if (blogUpdateTypeInput === 'video' && !blogYoutubeUrlInput.trim()) return;

    let imageUrl = blogImageUrlInput.trim() || undefined;

    if (blogImageFile) {
      setBlogImageUploading(true);
      try {
        const result = await entrantUpload({ file: blogImageFile, assetType: 'image' });
        imageUrl = result.asset.cdnUrl;
      } finally {
        setBlogImageUploading(false);
      }
    }

    if (blogFormMode === 'edit' && editingUpdateId) {
      await updateUpdateMutation.mutateAsync({
        updateId: editingUpdateId,
        title: blogTitleInput.trim(),
        updateType: blogUpdateTypeInput,
        content: blogContentInput.trim() || undefined,
        youtubeEmbedUrl: blogUpdateTypeInput === 'video' ? blogYoutubeUrlInput.trim() : undefined,
        imageUrl,
        isPinned: blogPinnedInput,
      });
      return;
    }

    await createUpdateMutation.mutateAsync({
      title: blogTitleInput.trim(),
      updateType: blogUpdateTypeInput,
      content: blogContentInput.trim() || undefined,
      youtubeEmbedUrl: blogUpdateTypeInput === 'video' ? blogYoutubeUrlInput.trim() : undefined,
      imageUrl,
      isPinned: blogPinnedInput,
    });
  }

  // Gallery lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [galleryExpanded, setGalleryExpanded] = useState(false);
  const GALLERY_PREVIEW_COUNT = 12;

  // GSAP animation refs
  const avatarRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressCardRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const blogPostsRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const donorsRef = useRef<HTMLDivElement>(null);
  const donateButtonRef = useRef<HTMLButtonElement>(null);

  // Sticky mobile donate header — appears after scrolling past the main donate button
  const [showStickyDonate, setShowStickyDonate] = useState(false);
  useEffect(() => {
    const el = donateButtonRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyDonate(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      if (avatarRef.current) {
        gsap.from(avatarRef.current, {
          scale: 0.85,
          opacity: 0,
          duration: 0.8,
          ease: 'back.out(1.7)',
          delay: 0.2,
        });
      }

      if (progressBarRef.current) {
        gsap.from(progressBarRef.current, {
          width: 0,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: progressBarRef.current,
            start: 'top 85%',
            once: true,
          },
        });
      }

      if (progressCardRef.current) {
        gsap.from(progressCardRef.current, {
          y: 30,
          opacity: 0,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: progressCardRef.current,
            start: 'top 85%',
            once: true,
          },
        });
      }

      if (statsRef.current) {
        gsap.from(statsRef.current.children, {
          y: 20,
          opacity: 0,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: statsRef.current,
            start: 'top 85%',
            once: true,
          },
        });
      }

      if (blogPostsRef.current) {
        gsap.from(blogPostsRef.current.children, {
          y: 30,
          opacity: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: blogPostsRef.current,
            start: 'top 85%',
            once: true,
          },
        });
      }

      if (galleryRef.current) {
        gsap.from(galleryRef.current.children, {
          scale: 0.92,
          opacity: 0,
          duration: 0.5,
          stagger: 0.08,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: galleryRef.current,
            start: 'top 85%',
            once: true,
          },
        });
      }

      if (donorsRef.current) {
        gsap.from(donorsRef.current.children, {
          y: 20,
          opacity: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: donorsRef.current,
            start: 'top 85%',
            once: true,
          },
        });
      }

      if (donateButtonRef.current) {
        gsap.to(donateButtonRef.current, {
          boxShadow: '0 0 20px hsl(48 58% 55% / 0.4)',
          duration: 1.5,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: 1,
        });
      }
    });

    return () => ctx.revert();
  }, []);

  if (viewType === 'team' || !!teamSlug) {
    return (
      <main className="flex-1 bg-white pb-20 md:pb-0">
        <section className="container max-w-5xl py-16">
          <p className="text-stone-500">{tCommon('error')}</p>
        </section>
      </main>
    );
  }

  if (isProfileLoading && !profileData) {
    return (
      <main className="flex-1 bg-white pb-20 md:pb-0">
        <section className="container max-w-5xl py-16">
          <p className="text-stone-500">{tCommon('loading')}</p>
        </section>
      </main>
    );
  }

  if (!profileData) {
    return (
      <main className="flex-1 bg-white pb-20 md:pb-0">
        <section className="container max-w-5xl py-16">
          <p className="text-stone-500">{tCommon('error')}</p>
        </section>
      </main>
    );
  }

  const fundraiser = profileData;
  const isPassengerProfile = Boolean(resolvedProfile?.linkedPrimaryEntrant);
  const storeEntrantId = authEntrant?.id ?? null;
  const storeEntrantUsername = authEntrant?.username ?? null;
  const resolvedProfileUsername = resolvedProfile?.username?.toLowerCase() ?? null;
  const isOwnProfile = Boolean(
    resolvedProfile &&
    ((storeEntrantId && storeEntrantId === resolvedProfile.id) ||
      (storeEntrantUsername &&
        resolvedProfileUsername &&
        storeEntrantUsername.toLowerCase() === resolvedProfileUsername)),
  );

  // Use API-computed progress which reflects the real campaign goal
  const progressPercent = resolvedProfile?.goalProgress ?? 0;

  // Use deterministic formatting on server, locale-aware after mount
  const formatCurrency = (amount: number) =>
    mounted ? `${currency.symbol}${amount.toLocaleString()}` : `${currency.symbol}${amount}`;

  const formatDate = (dateStr: string, options?: Intl.DateTimeFormatOptions) => {
    const date = new Date(dateStr);
    if (!mounted) {
      // Simple ISO-like format for SSR
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
    return date.toLocaleDateString('en-AU', options);
  };

  // Get display name
  const displayName = `${fundraiser.firstName} ${fundraiser.lastName}`;

  // Get profile initials
  const profileInitials = `${fundraiser.firstName[0]}${fundraiser.lastName[0]}`;

  return (
    <>
      {/* Sticky mobile donate header */}
      <div
        className={`fixed top-0 left-0 right-0 z-40 lg:hidden transition-transform duration-300 ${
          showStickyDonate ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="bg-uwr-orange/60 backdrop-blur-md border-b border-stone-200/60 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            {fundraiser.profileImage ? (
              <div className="relative w-7 h-7 rounded-full ring-2 ring-uwr-orange flex-shrink-0 overflow-hidden">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={fundraiser.profileImage}
                  alt={displayName}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            ) : resolvedProfile?.currentCampaign?.eventThumbnailUrl ? (
              <div className="relative w-7 h-7 rounded-full ring-2 ring-uwr-orange flex-shrink-0 overflow-hidden">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={resolvedProfile.currentCampaign.eventThumbnailUrl}
                  alt="Event thumbnail"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            ) : (
              <div className="w-7 h-7 rounded-full bg-uwr-orange/20 flex items-center justify-center text-uwr-orange text-xs font-bold flex-shrink-0">
                {profileInitials}
              </div>
            )}
            <span className="text-sm font-semibold text-stone-900 truncate">
              {t('supportName', { firstName: fundraiser.firstName })}
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setDonateInitialAmount(null);
              setIsDonateModalOpen(true);
            }}
            className="flex-shrink-0 h-8 text-xs font-semibold bg-uwr-orange/10 hover:bg-uwr-orange-light text-black"
          >
            <Heart className="w-3.5 h-3.5 mr-1.5" />
            {tDonate('donate')}
          </Button>
        </div>
      </div>

      <main className="flex-1 bg-white pb-20 md:pb-0">
        {/* Hero / Cover Section */}
        <section className="relative h-[300px] md:h-[400px]">
          <Image
            src={profileData.coverImage || '/images/uwr/HeroImageUWR.jpg'}
            alt="Cover"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent" />
        </section>

        {/* Profile Header */}
        <section className="relative -mt-32 md:-mt-24 pb-8 border-b border-stone-200">
          <div className="container max-w-5xl">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Profile Image — stacked square blocks */}
              <div ref={avatarRef} className="relative w-36 h-36 md:w-44 md:h-44 flex-shrink-0">
                <div className="w-full h-full rounded-full ring-4 ring-uwr-orange overflow-hidden bg-stone-100">
                  {profileData.profileImage ? (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={profileData.profileImage}
                        alt={displayName}
                        className="absolute inset-0 h-full w-full object-cover rounded-full"
                      />
                    </>
                  ) : resolvedProfile?.currentCampaign?.eventThumbnailUrl ? (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={resolvedProfile.currentCampaign.eventThumbnailUrl}
                        alt="Event thumbnail"
                        className="absolute inset-0 h-full w-full object-cover rounded-full"
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-stone-500">
                      {profileInitials}
                    </div>
                  )}
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 pt-2 md:pt-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-3xl md:text-4xl font-bold text-stone-900">
                        {displayName}
                      </h1>
                    </div>
                    <ShortUrlDisplay
                      url={`${getTenantConfig('uwr').fundraiserShortDomain ?? 'unitedweride.global'}/${fundraiser.username}`}
                    />
                  </div>
                  <div className="flex gap-2">
                    {/* Mobile Donate button - opens full modal */}
                    {strategy.isFundraisingActive && (
                      <div className="lg:hidden">
                        <Button
                          ref={donateButtonRef}
                          onClick={() => {
                            setDonateInitialAmount(null);
                            setIsDonateModalOpen(true);
                          }}
                          className="h-9 font-semibold"
                        >
                          <Heart className="w-4 h-4 mr-2" />
                          {tDonate('donate')}
                        </Button>
                      </div>
                    )}

                    {/* Share dropdown */}
                    <ShareDropdown
                      url={typeof window !== 'undefined' ? window.location.href : ''}
                      title={displayName}
                      campaign={getTenantConfig('uwr').displayName}
                      t={(key, values) => t(`share.${key}`, values)}
                    />
                  </div>
                </div>

                {/* Quick Stats - Different for team vs individual */}
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2 text-stone-500">
                    <MapPin className="w-4 h-4 text-uwr-orange" />
                    {profileData.currentEvent.eventSlug && profileData.currentEvent.country ? (
                      <Link
                        href={`/events/${toSlug(profileData.currentEvent.country)}/${profileData.currentEvent.eventSlug}`}
                        className="hover:text-uwr-orange transition-colors"
                      >
                        {profileData.currentEvent.city}, {profileData.currentEvent.country}
                      </Link>
                    ) : (
                      <span>
                        {profileData.currentEvent.city}, {profileData.currentEvent.country}
                      </span>
                    )}
                  </div>

                  {/* Individual: Show vehicle and riding since */}
                  {!isPassengerProfile && (
                    <div className="flex items-center gap-2 text-stone-500">
                      <Car className="w-4 h-4 text-uwr-orange" />
                      <span>
                        {fundraiser.vehicle.year} {fundraiser.vehicle.make}{' '}
                        {fundraiser.vehicle.model}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-stone-500">
                    <Calendar className="w-4 h-4 text-uwr-orange" />
                    <span>
                      {t('ridingSince')} {fundraiser.yearStarted}
                    </span>
                  </div>
                </div>

                {/* Riding With & Team - Inline for individuals */}
                {(fundraiser?.passenger || fundraiser?.team) && (
                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    {/* Riding With (Passenger) */}
                    {fundraiser?.passenger && (
                      <Link
                        href={`/fundraiser/${fundraiser.passenger.username}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/50 border border-stone-200 hover:bg-stone-100/50 transition-colors"
                      >
                        <UserPlus className="w-3.5 h-3.5 text-uwr-orange" />
                        <span className="text-stone-500 text-sm">{t('ridingWith')}</span>
                        <span className="text-stone-900 font-medium text-sm">
                          {fundraiser.passenger.firstName} {fundraiser.passenger.lastName}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-stone-500" />
                      </Link>
                    )}

                    {/* Team Membership */}
                    {fundraiser?.team && (
                      <Link
                        href={`/team/${fundraiser.team.slug}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/50 border border-stone-200 hover:bg-stone-100/50 transition-colors"
                      >
                        <Users className="w-3.5 h-3.5 text-uwr-orange" />
                        <span className="text-stone-500 text-sm">{t('memberOf')}</span>
                        <span className="text-stone-900 font-medium text-sm">
                          {fundraiser.team.name}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-stone-500" />
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8 md:py-12">
          <div className="container max-w-5xl">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Fundraising Progress Card */}
                <div
                  ref={progressCardRef}
                  className="bg-white rounded-xl p-6 border border-stone-200"
                >
                  <div className="flex justify-between items-end mb-3 gap-2">
                    <div className="min-w-0">
                      <p className="text-2xl font-bold text-uwr-orange">
                        {formatCurrency(profileData.currentEvent.raised)}
                      </p>
                      <p className="text-stone-500 text-xs md:text-sm">
                        {t('goal')} {formatCurrency(profileData.currentEvent.goal)}
                        {isOwnProfile &&
                        resolvedProfile?.goalSourceAmount &&
                        resolvedProfile?.goalSourceCurrency ? (
                          <span className="ml-1 text-stone-500">
                            ({resolvedProfile.goalSourceCurrency}{' '}
                            {resolvedProfile.goalSourceAmount.toLocaleString()})
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xl md:text-2xl font-bold text-stone-900">
                        {Math.round(progressPercent)}%
                      </p>
                      <p className="text-stone-500 text-xs md:text-sm">
                        {resolvedProfile?.donationCount ?? 0} {t('supporters').toLowerCase()}
                      </p>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        ref={progressBarRef}
                        className="h-full bg-gradient-to-r from-uwr-orange to-uwr-orange-light rounded-full transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <p className="text-stone-500 text-sm mt-2">
                      {t('percentOfGoal', { percent: Math.round(progressPercent) })}
                    </p>
                  </div>
                </div>

                {/* Historical Stats */}
                <div ref={statsRef} className="grid gap-4 grid-cols-2 md:grid-cols-3">
                  <div className="bg-white rounded-xl p-5 border border-stone-200 text-center">
                    <TrendingUp className="w-6 h-6 text-uwr-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-900">
                      {formatCurrency(Math.round(profileData.totalRaised))}
                    </p>
                    <p className="text-stone-500 text-sm">{t('totalRaised')}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-stone-200 text-center">
                    <Award className="w-6 h-6 text-uwr-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-900">{profileData.totalEvents}</p>
                    <p className="text-stone-500 text-sm">{t('events')}</p>
                  </div>
                  <div className="hidden md:block bg-white rounded-xl p-5 border border-stone-200 text-center">
                    <Calendar className="w-6 h-6 text-uwr-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-900">{fundraiser.yearStarted}</p>
                    <p className="text-stone-500 text-sm">{t('yearStarted')}</p>
                  </div>
                </div>

                {/* Blog Posts */}
                {(profileData.blogPosts.length > 0 || isOwnProfile) && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-stone-900">{t('updates')}</h2>
                      {isOwnProfile && (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPreviousUpdateId(null);
                              setIsPreviousUpdatesOpen(true);
                            }}
                          >
                            {t('previousUpdates')}
                          </Button>
                          <Button type="button" size="sm" onClick={openCreateUpdate}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('addUpdate')}
                          </Button>
                        </div>
                      )}
                    </div>
                    <div ref={blogPostsRef} className="space-y-4">
                      {profileData.blogPosts.length === 0 && (
                        <div className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-500">
                          {t('noUpdatesYet')}
                        </div>
                      )}
                      {profileData.blogPosts.map((post) => (
                        <div
                          key={post.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setSelectedPost(post);
                            setUpdateDialogOpen(true);
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' && event.key !== ' ') {
                              return;
                            }
                            event.preventDefault();
                            setSelectedPost(post);
                            setUpdateDialogOpen(true);
                          }}
                          className="group relative w-full text-left bg-white rounded-xl p-6 border border-stone-200 hover:bg-stone-100/50 transition-colors"
                        >
                          {isOwnProfile && (
                            <div className="absolute right-3 top-3 z-10 flex items-center gap-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                              <Button
                                type="button"
                                size="icon"
                                variant="secondary"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handlePinUpdate(post.id);
                                }}
                                disabled={pinUpdateMutation.isPending}
                                className="h-8 w-8 bg-stone-100/90 text-stone-700 hover:bg-stone-200 hover:text-uwr-orange"
                                aria-label={t('pinUpdate')}
                              >
                                <Pin
                                  className={`h-4 w-4 ${post.isPinned ? 'fill-uwr-orange text-uwr-orange' : ''}`}
                                />
                              </Button>
                              <Button
                                type="button"
                                size="icon"
                                variant="secondary"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleDuplicateUpdate(post.id);
                                }}
                                disabled={duplicateUpdateMutation.isPending}
                                className="h-8 w-8 bg-stone-100/90 text-stone-700 hover:bg-stone-200 hover:text-stone-900"
                                aria-label={t('duplicateUpdate')}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                size="icon"
                                variant="secondary"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openEditUpdate(post);
                                }}
                                className="h-8 w-8 bg-stone-100/90 text-stone-700 hover:bg-stone-200 hover:text-stone-900"
                                aria-label={tCommon('edit')}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-stone-500 text-sm mb-1">
                                {formatDate(post.date, {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </p>
                              <h3 className="text-stone-900 font-semibold mb-1">{post.title}</h3>
                              {post.isDefault && (
                                <p className="mb-1 text-xs font-medium text-uwr-orange">
                                  {t('defaultBlogLabel')}
                                </p>
                              )}
                              {post.isPinned && (
                                <p className="mb-1 text-xs font-medium text-uwr-orange">
                                  {t('pinnedUpdate')}
                                </p>
                              )}
                              {post.content && (
                                <p className="text-stone-500 text-sm line-clamp-3 mb-3">
                                  {isHtmlContent(post.content)
                                    ? stripHtml(post.content)
                                    : post.content}
                                </p>
                              )}
                            </div>
                            <ChevronRight className="w-5 h-5 text-stone-600 flex-shrink-0 mt-1" />
                          </div>
                          {post.updateType === 'video' &&
                            post.youtubeEmbedUrl &&
                            (() => {
                              const m = post.youtubeEmbedUrl.match(
                                /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/,
                              );
                              return m?.[1] ? (
                                <div className="aspect-video w-full overflow-hidden rounded-lg bg-stone-100">
                                  <iframe
                                    src={`https://www.youtube.com/embed/${m[1]}`}
                                    title={post.title}
                                    className="h-full w-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  />
                                </div>
                              ) : null;
                            })()}
                          {post.imageUrl && post.updateType !== 'video' && (
                            <div className="w-full overflow-hidden rounded-lg bg-stone-100">
                              <BlogPostImage src={post.imageUrl} alt={post.title} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gallery */}
                {(profileData.gallery.length > 0 || isOwnProfile) && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-stone-900">{t('gallery')}</h2>
                      {isOwnProfile && (
                        <Button size="sm" onClick={() => setIsGalleryUploadOpen(true)}>
                          <Plus className="w-4 h-4 mr-1" />
                          {t('addImages')}
                        </Button>
                      )}
                    </div>
                    {profileData.gallery.length === 0 && isOwnProfile ? (
                      <div className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-500">
                        {t('galleryEmpty')}
                      </div>
                    ) : (
                      <>
                        <div
                          ref={galleryRef}
                          className="columns-2 md:columns-4 gap-1.5 space-y-1.5"
                        >
                          {(galleryExpanded
                            ? profileData.gallery
                            : profileData.gallery.slice(0, GALLERY_PREVIEW_COUNT)
                          ).map((image, index) => (
                            <div
                              key={image.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setLightboxIndex(index);
                                setLightboxOpen(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setLightboxIndex(index);
                                  setLightboxOpen(true);
                                }
                              }}
                              className="relative break-inside-avoid rounded-md overflow-hidden bg-stone-100 group cursor-pointer"
                            >
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={image.url}
                                alt={image.caption || t('galleryImageAlt')}
                                className="w-full h-auto object-cover transition-transform group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute bottom-0 left-0 right-0 p-2">
                                  {image.caption && (
                                    <p className="text-white text-xs font-medium truncate">
                                      {image.caption}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {isOwnProfile && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirmGalleryId(image.id);
                                  }}
                                  className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {!galleryExpanded && profileData.gallery.length > GALLERY_PREVIEW_COUNT && (
                          <button
                            type="button"
                            onClick={() => setGalleryExpanded(true)}
                            className="mt-3 w-full py-2 text-sm font-medium text-stone-500 hover:text-stone-900 border border-stone-200 rounded-lg hover:border-stone-300 transition-colors"
                          >
                            Load more ({profileData.gallery.length - GALLERY_PREVIEW_COUNT} more)
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Donors List */}
                {resolvedProfile?.privacyShowDonors !== false && donorsTotal > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-stone-900">
                        <Heart className="w-5 h-5 inline mr-2 text-uwr-orange" />
                        {t('supporters')} ({donorsTotal})
                      </h2>
                    </div>
                    <div ref={donorsRef} className="space-y-3">
                      {profileData.donors.map((donor) => (
                        <div
                          key={donor.id}
                          className="bg-white rounded-xl p-4 border border-stone-200 flex items-start gap-4"
                        >
                          <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                            {donor.anonymous ? (
                              <Heart className="w-5 h-5 text-stone-500" />
                            ) : (
                              <span className="text-stone-500 font-medium">
                                {donor.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-stone-900 font-medium">
                                {donor.anonymous ? tCommon('anonymous') : donor.name}
                              </p>
                              <p className="text-uwr-orange font-semibold">
                                {formatCurrency(Math.round(donor.amount))}
                              </p>
                            </div>
                            {donor.message && (
                              <p className="text-stone-500 text-sm mt-1">
                                &ldquo;{donor.message}&rdquo;
                              </p>
                            )}
                            <p className="text-stone-600 text-xs mt-1">
                              {formatDate(donor.date, {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {hasMoreDonors && (
                      <div className="mt-4 flex justify-center">
                        <Button
                          variant="outline"
                          onClick={() => void fetchNextDonorsPage()}
                          disabled={isFetchingMoreDonors}
                          className="bg-white border-stone-200 text-stone-900 hover:bg-stone-100 hover:text-stone-900"
                        >
                          {isFetchingMoreDonors ? tCommon('loading') : t('loadMoreSupporters')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {/* Event Sponsors */}
                <SponsorLogosSection eventId={donationEventId} variant="dark" />
              </div>

              {/* Right Column - Donation Form (Sticky) - Desktop only */}
              <div className="hidden lg:block lg:col-span-1">
                <div className="sticky top-24 space-y-4">
                  {strategy.isFundraisingActive ? (
                    <DonationAmountCard
                      recipientName={fundraiser.firstName}
                      currency={strategy.currency}
                      onDonate={(amount) => {
                        setDonateInitialAmount(amount);
                        setIsDonateModalOpen(true);
                      }}
                    />
                  ) : (
                    <div className="bg-white rounded-xl p-6 border border-stone-200 text-center">
                      <p className="text-stone-500 text-sm">{tDonate('fundraisingDisabled')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <DonationFormModal
        open={isDonateModalOpen}
        onOpenChange={(open) => {
          setIsDonateModalOpen(open);
          if (!open) setDonateInitialAmount(null);
        }}
        initialAmount={donateInitialAmount}
        recipientName={fundraiser.firstName}
        strategy={strategy}
        onSubmit={handleDonationSuccess}
        returnDonationId={returnDonationId ?? undefined}
        redirectResult={redirectResult}
        returnSessionId={returnSessionId}
        recoveryToken={recoveryToken}
        isLocalStorageRecovery={isLocalStorageRecovery}
        onRedirectSuccess={handleRedirectSuccess}
      />

      <Dialog open={isUpdateDialogOpen} onOpenChange={setIsUpdateDialogOpen}>
        <DialogContent className="max-w-2xl border-stone-300 bg-white text-stone-900 max-h-[90dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{blogFormMode === 'edit' ? t('editUpdate') : t('addUpdate')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 -mx-5 px-5">
            <div className="space-y-2">
              <Label htmlFor="update-type">{t('updateType')}</Label>
              <select
                id="update-type"
                value={blogUpdateTypeInput}
                onChange={(event) => setBlogUpdateTypeInput(event.target.value as 'blog' | 'video')}
                className="h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm text-stone-900"
              >
                <option value="blog">{t('blogType')}</option>
                <option value="video">{t('videoType')}</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="blog-title">{t('blogTitle')}</Label>
              <Input
                id="blog-title"
                value={blogTitleInput}
                onChange={(event) => setBlogTitleInput(event.target.value)}
                placeholder={t('blogTitlePlaceholder')}
                className="border-stone-300 bg-white text-stone-900 placeholder:text-stone-400"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('blogContent')}</Label>
              <BlogEditor
                content={blogContentInput}
                onChange={setBlogContentInput}
                placeholder={t('blogContentPlaceholder')}
              />
            </div>

            {blogUpdateTypeInput === 'video' && (
              <div className="space-y-2">
                <Label htmlFor="blog-youtube-url">{t('youtubeUrl')}</Label>
                <Input
                  id="blog-youtube-url"
                  value={blogYoutubeUrlInput}
                  onChange={(event) => setBlogYoutubeUrlInput(event.target.value)}
                  placeholder={t('youtubeUrlPlaceholder')}
                  className="border-stone-300 bg-white text-stone-900 placeholder:text-stone-400"
                />
                {(() => {
                  const match = blogYoutubeUrlInput.match(
                    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/,
                  );
                  const videoId = match?.[1];
                  if (!videoId) return null;
                  return (
                    <div className="aspect-video w-full overflow-hidden rounded-lg border border-stone-300">
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title="YouTube preview"
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                })()}
              </div>
            )}

            {blogUpdateTypeInput === 'blog' && (
              <div className="space-y-2">
                <Label>{t('updateImageUrl')}</Label>
                {blogImageFile || blogImageUrlInput ? (
                  <div className="relative overflow-hidden rounded-lg border border-stone-300">
                    <BlogPostImage
                      src={blogImageFile ? URL.createObjectURL(blogImageFile) : blogImageUrlInput}
                      alt="Update image preview"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setBlogImageFile(null);
                        setBlogImageUrlInput('');
                      }}
                      className="absolute right-2 top-2 rounded-full bg-white/80 p-1.5 text-stone-900 hover:bg-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="blog-image-upload"
                    className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-stone-300 bg-white p-6 text-stone-500 transition-colors hover:border-[#A07D21] hover:text-stone-600"
                  >
                    <ImagePlus className="h-8 w-8" />
                    <span className="text-sm">Click to upload an image</span>
                    <input
                      id="blog-image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setBlogImageFile(file);
                          setBlogImageUrlInput('');
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-stone-600">
              <input
                type="checkbox"
                checked={blogPinnedInput}
                onChange={(event) => setBlogPinnedInput(event.target.checked)}
                className="h-4 w-4 rounded border-stone-300 bg-white text-uwr-orange focus:ring-uwr-orange"
              />
              {t('pinUpdate')}
            </label>

            {(updateUpdateMutation.error ||
              createUpdateMutation.error ||
              deleteUpdateMutation.error ||
              duplicateUpdateMutation.error) && (
              <p className="text-sm text-red-400" role="alert">
                {updateUpdateMutation.error?.message ??
                  createUpdateMutation.error?.message ??
                  deleteUpdateMutation.error?.message ??
                  duplicateUpdateMutation.error?.message ??
                  tCommon('error')}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-stone-300/50 pt-4">
            {blogFormMode === 'edit' ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsDeleteConfirmDialogOpen(true)}
                disabled={
                  deleteUpdateMutation.isPending ||
                  updateUpdateMutation.isPending ||
                  createUpdateMutation.isPending
                }
                className="text-red-400 hover:bg-red-900/30 hover:text-red-300"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {tCommon('delete')}
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsUpdateDialogOpen(false)}
                className="border-stone-300 bg-transparent text-stone-600 hover:bg-stone-100 hover:text-stone-900"
              >
                {tCommon('cancel')}
              </Button>
              <button
                type="button"
                onClick={() => void handleSaveUpdate()}
                disabled={
                  !blogTitleInput.trim() ||
                  (blogUpdateTypeInput === 'blog' && !blogContentInput.trim()) ||
                  (blogUpdateTypeInput === 'video' && !blogYoutubeUrlInput.trim()) ||
                  blogImageUploading ||
                  createUpdateMutation.isPending ||
                  updateUpdateMutation.isPending ||
                  deleteUpdateMutation.isPending
                }
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-uwr-orange px-4 py-2 text-sm font-medium text-stone-900 transition-colors hover:bg-uwr-orange-light disabled:pointer-events-none disabled:opacity-40"
              >
                {blogImageUploading ||
                updateUpdateMutation.isPending ||
                createUpdateMutation.isPending ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {blogImageUploading ? 'Uploading...' : tCommon('loading')}
                  </span>
                ) : (
                  tCommon('save')
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteConfirmDialogOpen} onOpenChange={setIsDeleteConfirmDialogOpen}>
        <DialogContent className="max-w-md border-stone-300 bg-white text-stone-900">
          <DialogHeader>
            <DialogTitle>{tCommon('delete')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">{t('deleteUpdateConfirm')}</p>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteConfirmDialogOpen(false)}
              disabled={deleteUpdateMutation.isPending}
              className="border-stone-300 bg-transparent text-stone-600 hover:bg-stone-100 hover:text-stone-900"
            >
              {tCommon('cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void handleDeleteUpdate()}
              disabled={deleteUpdateMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-500"
            >
              {deleteUpdateMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {tCommon('loading')}
                </span>
              ) : (
                tCommon('delete')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isPreviousUpdatesOpen} onOpenChange={setIsPreviousUpdatesOpen}>
        <DialogContent className="max-w-4xl border-stone-300 bg-white text-stone-900 max-h-[90dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('previousUpdates')}</DialogTitle>
          </DialogHeader>

          {previousUpdates.length === 0 ? (
            <p className="text-sm text-stone-500 py-8 text-center">{t('noPreviousUpdates')}</p>
          ) : (
            <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0 overflow-hidden">
              {/* Mobile: dropdown selector */}
              <div className="md:hidden">
                <Label htmlFor="previous-update-select">{t('selectPreviousUpdate')}</Label>
                <select
                  id="previous-update-select"
                  value={selectedPreviousUpdate?.id ?? ''}
                  onChange={(e) => setSelectedPreviousUpdateId(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm text-stone-900"
                >
                  {previousUpdates.map((update) => (
                    <option key={update.id} value={update.id}>
                      {update.title} — {update.campaignName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Desktop: list pane (1/3) */}
              <div className="hidden md:block w-1/3 overflow-y-auto border border-stone-200 rounded-lg">
                <ul className="divide-y divide-stone-800">
                  {previousUpdates.map((update) => {
                    const isSelected = selectedPreviousUpdate?.id === update.id;
                    return (
                      <li key={update.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedPreviousUpdateId(update.id)}
                          className={`w-full text-left px-3 py-2.5 transition-colors ${
                            isSelected ? 'bg-stone-100' : 'hover:bg-stone-100/60'
                          }`}
                        >
                          <p className="text-sm font-medium text-stone-900 truncate">
                            {update.title}
                          </p>
                          <p className="text-xs text-stone-500 truncate">
                            {update.campaignName} ·{' '}
                            {new Date(update.createdAt).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Content pane (2/3) */}
              <div className="md:w-2/3 flex-1 overflow-y-auto border border-stone-200 rounded-lg p-4 min-h-[200px]">
                {selectedPreviousUpdate ? (
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-semibold text-stone-900">
                        {selectedPreviousUpdate.title}
                      </h3>
                      <p className="text-xs text-stone-500">
                        {selectedPreviousUpdate.campaignName} ·{' '}
                        {new Date(selectedPreviousUpdate.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    {selectedPreviousUpdate.content &&
                      (isHtmlContent(selectedPreviousUpdate.content) ? (
                        mounted ? (
                          <div
                            className="prose prose-invert max-w-none prose-p:text-stone-600 prose-headings:text-stone-900 prose-a:text-blue-400 prose-strong:text-stone-900 text-sm"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(selectedPreviousUpdate.content),
                            }}
                          />
                        ) : null
                      ) : (
                        <p className="whitespace-pre-wrap leading-relaxed text-stone-600 text-sm">
                          {selectedPreviousUpdate.content}
                        </p>
                      ))}
                    {selectedPreviousUpdate.updateType === 'video' &&
                      selectedPreviousUpdate.youtubeEmbedUrl && (
                        <p className="text-xs text-stone-500 break-all">
                          {selectedPreviousUpdate.youtubeEmbedUrl}
                        </p>
                      )}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500">{t('selectPreviousUpdate')}</p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-stone-300/50 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPreviousUpdatesOpen(false)}
              className="border-stone-300 bg-transparent text-stone-600 hover:bg-stone-100 hover:text-stone-900"
            >
              {tCommon('cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => copyPreviousUpdateToNew(selectedPreviousUpdate)}
              disabled={!selectedPreviousUpdate}
              className="bg-uwr-orange text-white hover:bg-uwr-orange-light"
            >
              <Copy className="mr-2 h-4 w-4" />
              {t('copyUpdateToNew')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent className="max-w-3xl border-stone-300 bg-white text-stone-900">
          <DialogHeader>
            <DialogTitle>{t('update')}</DialogTitle>
          </DialogHeader>
          {selectedPost && (
            <div className="max-h-[75vh] overflow-y-auto pt-2 space-y-4">
              <p className="text-sm text-stone-500">
                {formatDate(selectedPost.date, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              <h2 className="text-2xl font-bold text-stone-900">{selectedPost.title}</h2>
              {selectedPost.content &&
                (isHtmlContent(selectedPost.content) ? (
                  mounted ? (
                    <div
                      className="prose prose-invert max-w-none prose-p:text-stone-600 prose-headings:text-stone-900 prose-a:text-blue-400 prose-strong:text-stone-900"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedPost.content) }}
                    />
                  ) : (
                    <div className="prose prose-invert max-w-none prose-p:text-stone-600 prose-headings:text-stone-900 prose-a:text-blue-400 prose-strong:text-stone-900" />
                  )
                ) : (
                  <p className="whitespace-pre-wrap leading-relaxed text-stone-600">
                    {selectedPost.content}
                  </p>
                ))}
              {selectedPost.updateType === 'video' &&
                selectedPost.youtubeEmbedUrl &&
                (() => {
                  const m = selectedPost.youtubeEmbedUrl.match(
                    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/,
                  );
                  const vid = m?.[1];
                  return vid ? (
                    <div className="aspect-video w-full overflow-hidden rounded-xl bg-white">
                      <iframe
                        src={`https://www.youtube.com/embed/${vid}`}
                        title={selectedPost.title}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </div>
                  ) : null;
                })()}
              {selectedPost.imageUrl && selectedPost.updateType !== 'video' && (
                <div className="w-full overflow-hidden rounded-xl bg-stone-100">
                  <BlogPostImage src={selectedPost.imageUrl} alt={selectedPost.title} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gallery Upload Dialog */}
      <Dialog
        open={isGalleryUploadOpen}
        onOpenChange={(open) => {
          if (!galleryUploading) setIsGalleryUploadOpen(open);
        }}
      >
        <DialogContent className="bg-white border-stone-300 text-stone-900">
          <DialogHeader>
            <DialogTitle>{t('uploadImages')}</DialogTitle>
          </DialogHeader>

          {galleryUploading ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">
                    Uploading {Math.min(galleryUploadProgress + 1, galleryFiles.length)} of{' '}
                    {galleryFiles.length}
                  </span>
                  <span className="text-stone-900 font-medium">
                    {Math.round((galleryUploadProgress / galleryFiles.length) * 100)}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-uwr-orange transition-all duration-300 ease-out"
                    style={{ width: `${(galleryUploadProgress / galleryFiles.length) * 100}%` }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {galleryFiles.map((file, i) => (
                  <div
                    key={i}
                    className="relative aspect-square rounded-md overflow-hidden bg-stone-100"
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div
                      className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                        i < galleryUploadProgress
                          ? 'bg-black/40'
                          : i === galleryUploadProgress
                            ? 'bg-black/30'
                            : 'bg-black/60'
                      }`}
                    >
                      {i < galleryUploadProgress ? (
                        <Check className="w-5 h-5 text-green-400" />
                      ) : i === galleryUploadProgress ? (
                        <Loader2 className="w-5 h-5 text-uwr-orange animate-spin" />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-dashed border-stone-300 p-6 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? []).slice(0, 10);
                    setGalleryFiles(files);
                  }}
                  className="hidden"
                  id="gallery-file-input"
                />
                <label
                  htmlFor="gallery-file-input"
                  className="cursor-pointer text-stone-500 hover:text-stone-900 transition-colors"
                >
                  <Plus className="w-8 h-8 mx-auto mb-2" />
                  <p>{t('addImages')}</p>
                  <p className="text-xs text-stone-500 mt-1">{t('maxImagesError')}</p>
                </label>
              </div>
              {galleryFiles.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {galleryFiles.map((file, i) => (
                    <div
                      key={i}
                      className="relative aspect-square rounded-lg overflow-hidden bg-stone-100"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setGalleryFiles((prev) => prev.filter((_, idx) => idx !== i))
                        }
                        className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-uwr-orange px-4 py-2 text-sm font-medium text-stone-900 transition-colors hover:bg-uwr-orange-light disabled:pointer-events-none disabled:opacity-40"
                disabled={galleryFiles.length === 0}
                onClick={() => void handleGalleryUpload()}
              >
                {t('uploadImages')}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gallery Delete Confirmation */}
      <Dialog
        open={!!deleteConfirmGalleryId}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmGalleryId(null);
        }}
      >
        <DialogContent className="bg-white border-stone-300 text-stone-900">
          <DialogHeader>
            <DialogTitle>{t('deleteImage')}</DialogTitle>
          </DialogHeader>
          <p className="text-stone-500">{t('deleteImageConfirm')}</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmGalleryId(null)}
              className="border-stone-300 bg-transparent text-stone-600 hover:bg-stone-100 hover:text-stone-900"
            >
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={removeGalleryImageMutation.isPending}
              onClick={() => {
                if (deleteConfirmGalleryId) {
                  removeGalleryImageMutation.mutate({ galleryItemId: deleteConfirmGalleryId });
                }
              }}
            >
              {removeGalleryImageMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t('deleteImage')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Lightbox */}
      {lightboxOpen && profileData.gallery.length > 0 && (
        <LightboxKeyHandler
          galleryLength={profileData.gallery.length}
          onPrev={() =>
            setLightboxIndex((prev) => (prev === 0 ? profileData.gallery.length - 1 : prev - 1))
          }
          onNext={() =>
            setLightboxIndex((prev) => (prev === profileData.gallery.length - 1 ? 0 : prev + 1))
          }
          onClose={() => setLightboxOpen(false)}
        />
      )}
      {lightboxOpen && profileData.gallery.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex flex-col"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 flex-shrink-0">
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-stone-900" />
            </button>
            <span className="text-stone-900/70 text-sm">
              {lightboxIndex + 1} / {profileData.gallery.length}
            </span>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>

          {/* Image Container */}
          <div
            className="flex-1 flex items-center justify-center px-4 min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Previous Button */}
            <button
              type="button"
              onClick={() =>
                setLightboxIndex((prev) => (prev === 0 ? profileData.gallery.length - 1 : prev - 1))
              }
              className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
            >
              <ChevronLeft className="w-6 h-6 text-stone-900" />
            </button>

            {/* Current Image */}
            <div className="relative w-full h-full max-w-4xl">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={profileData.gallery[lightboxIndex].url}
                alt={profileData.gallery[lightboxIndex].caption || 'Gallery image'}
                className="absolute inset-0 h-full w-full object-contain"
              />
            </div>

            {/* Next Button */}
            <button
              type="button"
              onClick={() =>
                setLightboxIndex((prev) => (prev === profileData.gallery.length - 1 ? 0 : prev + 1))
              }
              className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
            >
              <ChevronRight className="w-6 h-6 text-stone-900" />
            </button>
          </div>

          {/* Caption */}
          <div className="p-4 text-center flex-shrink-0">
            {profileData.gallery[lightboxIndex].caption && (
              <p className="text-stone-900 font-medium">
                {profileData.gallery[lightboxIndex].caption}
              </p>
            )}
            <p className="text-stone-500 text-sm">{profileData.gallery[lightboxIndex].year}</p>
          </div>
        </div>
      )}
    </>
  );
}
